import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, MapPin, User, Building2, Calendar, Phone, Hash, Clock, Tag, Timer,
  Plus, Trash2, Upload, Image as ImageIcon, History, Package, Receipt, ListChecks, MessageSquare, Send, Pencil, Copy, Boxes, Search, ExternalLink, ZoomIn, CheckCircle2, AlertTriangle, ArrowLeftRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { useAuth } from "@/context/AuthContext";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { AttachmentLightbox, attUrl } from "@/components/AttachmentLightbox";
import PageHeader from "@/components/PageHeader";
import { StatoBadge, PrioritaBadge } from "@/components/Badges";
import { STATUS, PRIORITY, ROLE_LABEL, STATUS_LABEL, PICCO_OPTIONS } from "@/lib/constants";
import { useStatuses } from "@/context/StatusContext";
import { endTime, fmtDate, fmtDateTime } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProductCombobox } from "@/components/ProductCombobox";
import { cn } from "@/lib/utils";
import { DateField, TimeField } from "@/components/DateTimeField";
import { RelatedTimeline } from "@/components/RelatedTimeline";
import { MaintenanceBanner } from "@/components/MaintenanceBanner";

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { options: statusOptions, reasons: suspensionReasons, koReasons } = useStatuses();
  const [t, setT] = useState(null);
  const [tecnici, setTecnici] = useState([]);
  const [committenti, setCommittenti] = useState([]);
  const [categories, setCategories] = useState([]);
  const [atts, setAtts] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [listino, setListino] = useState([]);
  const [selVoce, setSelVoce] = useState("");
  const [actQty, setActQty] = useState(1);
  const [noteText, setNoteText] = useState("");
  const [editNoteId, setEditNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedNotes, setDeletedNotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [pmSearch, setPmSearch] = useState("");
  const [winv, setWinv] = useState([]);
  const [selProd, setSelProd] = useState("");
  const [wSerials, setWSerials] = useState([]);
  const [wQty, setWQty] = useState(1);
  const [paidDialog, setPaidDialog] = useState(false);
  const [benForm, setBenForm] = useState({ benestare_number: "", benestare_date: "" });
  const fileRef = useRef(null);
  const canManage = ["admin", "responsabile"].includes(user?.role);
  const [devicePwd, setDevicePwd] = useState("");
  const [idServizi, setIdServizi] = useState("");
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendMotivo, setSuspendMotivo] = useState("");
  const [koOpen, setKoOpen] = useState(false);
  const [koNote, setKoNote] = useState("");
  const [koMotivo, setKoMotivo] = useState("");
  const [related, setRelated] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [interMap, setInterMap] = useState({});

  const loadAtts = () => api.get(`/tickets/${id}/attachments`).then((r) => setAtts(r.data)).catch(() => {});
  useEffect(() => {
    api.get(`/tickets/${id}`).then((r) => {
      setT(r.data);
      // Se l'URL era un codice ticket (es. TK-0438, da link esterni/Magazzino), normalizza all'id interno
      if (r.data?.id && r.data.id !== id) navigate(`/tickets/${r.data.id}`, { replace: true, state: location.state });
    }).catch(() => {});
    api.get("/tecnici").then((r) => setTecnici(r.data)).catch(() => {});
    api.get("/committenti").then((r) => setCommittenti(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/magazzino/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
    api.get("/product-groups/map").then((r) => setInterMap(r.data || {})).catch(() => setInterMap({}));
    loadAtts();
  }, [id]);
  useEffect(() => { setDevicePwd(t?.device_password || ""); setIdServizi(t?.id_servizi || ""); }, [t?.device_password, t?.id_servizi]);
  useEffect(() => {
    if (!t) return;
    setRelated((prev) => prev.filter((x) => x.id !== t.id));
    if (!(t.id_olo || t.id_risorsa || t.client_name)) { setRelated([]); return; }
    api.get("/tickets/duplicates", { params: { id_olo: t.id_olo || "", id_risorsa: t.id_risorsa || "", client_name: t.client_name || "" } })
      .then((r) => setRelated((r.data || []).filter((x) => x.id !== t.id)))
      .catch(() => setRelated([]));
  }, [t?.id, t?.id_olo, t?.id_risorsa, t?.client_name]);
  useLiveRefresh(() => {
    api.get(`/tickets/${id}`).then((r) => setT(r.data)).catch(() => {});
    loadAtts();
  });

  const savePlanned = async (list) => {
    try {
      const { data } = await api.put(`/tickets/${id}/planned-materials`, { planned_materials: list.map(({ id: _i, ...m }) => m) });
      setT(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const addPlanned = (p) => {
    const list = [...(t.planned_materials || []), { product_id: p.id, sku: p.sku, name: p.name, description: p.name, manufacturer_code: p.manufacturer_code || "", supplier_name: p.supplier_name || "", unit: p.unit_of_measure || "pz", qty: 1, price: p.price || 0 }];
    savePlanned(list); setPmSearch("");
  };
  const setPlannedQty = (idx, qty) => savePlanned((t.planned_materials || []).map((m, i) => (i === idx ? { ...m, qty: parseFloat(qty) || 1 } : m)));
  const delPlanned = (idx) => savePlanned((t.planned_materials || []).filter((_, i) => i !== idx));

  useEffect(() => {
    if (t?.committente_id) api.get("/listino", { params: { committente_id: t.committente_id } }).then((r) => setListino(r.data)).catch(() => setListino([]));
    else setListino([]);
  }, [t?.committente_id]);

  const patch = async (data, msg) => { try { const { data: res } = await api.put(`/tickets/${id}`, data); setT(res); if (msg) toast.success(msg); } catch (e) { toast.error(apiError(e)); } };

  useEffect(() => {
    const wid = tecnici.find((x) => x.id === t?.assigned_to)?.magazzino_warehouse_id || "";
    if (wid) api.get("/magazzino/inventory", { params: { warehouse_id: wid } }).then((r) => setWinv(r.data)).catch(() => setWinv([]));
    else setWinv([]);
  }, [t?.assigned_to, tecnici]);

  const warehouseProducts = useMemo(() => {
    const map = {};
    (winv || []).forEach((r) => {
      const k = r.product_id;
      if (!map[k]) map[k] = { product_id: k, sku: r.sku, name: r.name, manufacturer_code: r.manufacturer_code, supplier_name: r.supplier_name, is_serialized: r.is_serialized, unit: r.unit_of_measure || "pz", price: r.price || 0, serials: [], qty: 0 };
      map[k].qty += r.stock || 0;
      if (r.serial_number) map[k].serials.push(r.serial_number);
    });
    return Object.values(map).filter((p) => p.qty > 0 || p.serials.length).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [winv]);
  const curProd = warehouseProducts.find((p) => p.product_id === selProd);
  const toggleWSerial = (s) => setWSerials((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  const addFromWarehouse = async () => {
    if (!curProd) { toast.error("Seleziona un materiale dal magazzino"); return; }
    const serials = curProd.is_serialized ? wSerials : [];
    if (curProd.is_serialized && serials.length === 0) { toast.error("Seleziona almeno un seriale"); return; }
    const qty = curProd.is_serialized ? serials.length : (parseFloat(wQty) || 1);
    try {
      const { data: res } = await api.post(`/tickets/${id}/materials`, {
        product_id: curProd.product_id, sku: curProd.sku, name: curProd.name, description: curProd.name,
        unit: curProd.unit, qty, price: curProd.price || 0, serial_numbers: serials,
      });
      setT(res); setSelProd(""); setWSerials([]); setWQty(1); toast.success("Materiale aggiunto");
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = () => {
    setEditForm({
      client_name: t.client_name || "", client_phone: t.client_phone || "", committente_id: t.committente_id || "",
      category: t.category || "", address: t.address || "", comune: t.comune || "",
      id_olo: t.id_olo || "", id_risorsa: t.id_risorsa || "", pfs: t.pfs || "", picco: t.picco || "", scheduled_date: t.scheduled_date || "",
      appointment_time: t.appointment_time || "", intervention_date: t.intervention_date || "", start_time: t.start_time || "", duration_min: t.duration_min || 120, description: t.description || "",
    });
    setEditOpen(true);
  };
  // Dopo una duplicazione: apri automaticamente la modale di modifica sul nuovo ticket
  useEffect(() => {
    if (t && canManage && location.state?.openEditOnLoad) {
      openEdit();
      navigate(location.pathname, { replace: true, state: { from: location.state?.from } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t?.id, location.state]);
  const saveEdit = async () => {
    try {
      const payload = { ...editForm, duration_min: parseInt(editForm.duration_min) || 120 };
      const { data: res } = await api.put(`/tickets/${id}`, payload);
      setT(res); setEditOpen(false); toast.success("Ticket aggiornato");
    } catch (e) { toast.error(apiError(e)); }
  };
  const duplicate = async () => {
    if (!window.confirm(`Duplicare il ticket ${t.code}? Verrà creato un nuovo ticket con le stesse informazioni.`)) return;
    try {
      const { data: created } = await api.post(`/tickets/${id}/duplicate`);
      toast.success(`Ticket duplicato: ${created.code}`);
      navigate(`/tickets/${created.id}`, { state: { openEditOnLoad: true } });
    } catch (e) { toast.error(apiError(e)); }
  };
  const changeStatus = async (status, extra = {}) => { try { const { data: res } = await api.post(`/tickets/${id}/status`, { status, ...extra }); setT(res); toast.success("Stato aggiornato"); return true; } catch (e) { toast.error(apiError(e)); return false; } };
  const onStatusChange = (status) => {
    if (status === "sospeso") { setSuspendReason(""); setSuspendMotivo(""); setSuspendOpen(true); return; }
    if (status === "ko") { setKoNote(""); setKoMotivo(""); setKoOpen(true); return; }
    if (status === "sospeso_manutenzione" && !window.confirm(`Inviare ${t.code} a Manutenzione? Verrà creata una copia da assegnare e questo ticket resterà sospeso fino al completamento della manutenzione.`)) return;
    changeStatus(status);
  };
  const submitKo = async () => {
    if (!koMotivo) { toast.error("Seleziona un motivo di KO"); return; }
    if (!koNote.trim()) { toast.error("Indica il motivo del KO"); return; }
    const ok = await changeStatus("ko", { note: koNote, ko_reason: koMotivo });
    if (ok) setKoOpen(false);
  };
  const submitSuspend = async () => {
    if (!suspendMotivo) { toast.error("Seleziona un motivo di sospensione"); return; }
    if (!suspendReason.trim()) { toast.error("Scrivi una nota di sospensione"); return; }
    const ok = await changeStatus("sospeso", { note: suspendReason, suspension_reason: suspendMotivo });
    if (ok) setSuspendOpen(false);
  };

  const delMaterial = async (mid) => { try { const { data: res } = await api.delete(`/tickets/${id}/materials/${mid}`); setT(res); } catch (e) { toast.error(apiError(e)); } };

  const addActivity = async () => {
    const voce = listino.find((v) => v.id === selVoce);
    if (!voce) { toast.error("Seleziona una voce di listino"); return; }
    try {
      const { data: res } = await api.post(`/tickets/${id}/activities`, {
        description: voce.description, code: voce.code, unit: voce.unit,
        listino_id: voce.id, qty: parseFloat(actQty) || 1, price: voce.price,
      });
      setT(res); setSelVoce(""); setActQty(1); toast.success("Voce aggiunta al computo");
    } catch (e) { toast.error(apiError(e)); }
  };
  const delActivity = async (aid) => { try { const { data: res } = await api.delete(`/tickets/${id}/activities/${aid}`); setT(res); } catch (e) { toast.error(apiError(e)); } };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try { const { data: res } = await api.post(`/tickets/${id}/notes`, { text: noteText }); setT(res); setNoteText(""); toast.success("Nota aggiunta"); } catch (e) { toast.error(apiError(e)); }
  };
  const canModifyNote = (n) => canManage || n.author_id === user?.id || (n.role === "tecnico" && n.author === user?.name);
  const saveNoteEdit = async (nid) => {
    if (!editNoteText.trim()) return;
    try { const { data: res } = await api.put(`/tickets/${id}/notes/${nid}`, { text: editNoteText }); setT(res); setEditNoteId(null); setEditNoteText(""); toast.success("Nota aggiornata"); } catch (e) { toast.error(apiError(e)); }
  };
  const deleteNote = async (nid) => {
    if (!window.confirm("Eliminare questa nota?")) return;
    try { const { data: res } = await api.delete(`/tickets/${id}/notes/${nid}`); setT(res); toast.success("Nota eliminata"); if (showDeleted) loadDeleted(); } catch (e) { toast.error(apiError(e)); }
  };
  const loadDeleted = async () => {
    try { const { data } = await api.get(`/tickets/${id}/deleted-notes`); setDeletedNotes(data); } catch (e) { toast.error(apiError(e)); }
  };
  const toggleDeleted = () => {
    const next = !showDeleted;
    setShowDeleted(next);
    if (next) loadDeleted();
  };

  const upload = async (e) => {
    const raw = e.target.files?.[0]; if (!raw) return;
    const file = await compressImage(raw);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/tickets/${id}/attachments`, fd); toast.success("Foto caricata"); loadAtts(); } catch (er) { toast.error(apiError(er)); }
    e.target.value = "";
  };
  const delAtt = async (aid) => { try { await api.delete(`/attachments/${aid}`); loadAtts(); } catch (er) { toast.error(apiError(er)); } };

  if (!t) return <Skeleton className="h-96 rounded-xl" />;

  const Info = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-muted-foreground"><Icon size={16} /></span>
      <div><p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</p><div className="font-semibold text-navy">{value || "—"}</div></div>
    </div>
  );
  const matTotal = (t.materials_used || []).reduce((s, m) => s + (m.price || 0) * (m.qty || 0), 0);
  const actTotal = (t.activities || []).reduce((s, a) => s + (a.price || 0) * (a.qty || 0), 0);
  const substituteForName = (pid) => {
    if (!pid) return null;
    const planned = t.planned_materials || [];
    if (planned.some((p) => p.product_id === pid)) return null;
    const group = interMap[pid] || [pid];
    const a = planned.find((p) => p.product_id && group.includes(p.product_id));
    return a ? a.name : null;
  };

  const backPath = location.state?.from || (t.area === "manutenzione" ? "/manutenzione" : "/tickets");
  const backLabel = { "/pianificazione": "Torna alla Pianificazione - Delivery & Assurance", "/manutenzione": "Torna a Ticket - Manutenzione" }[backPath] || "Torna a Ticket - Delivery & Assurance";
  return (
    <div className="space-y-6 animate-fade-up">
      <button onClick={() => navigate(backPath)} className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground" data-testid="back-btn">
        <ArrowLeft size={16} /> {backLabel}
      </button>

      <PageHeader overline={`${t.code}${t.id_olo ? ` · ID OLO ${t.id_olo}` : ""}`} title={t.client_name} subtitle={t.category}>
        <div className="flex flex-wrap items-end gap-2">
          {canManage && <Button variant="outline" className="h-9" onClick={openEdit} data-testid="edit-ticket-btn"><Pencil size={16} />Modifica</Button>}
          {canManage && <Button variant="outline" className="h-9" onClick={duplicate} data-testid="duplicate-ticket-btn"><Copy size={16} />Duplica</Button>}
          <div className="space-y-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Stato</p>
            <Select value={t.status} onValueChange={onStatusChange}>
              <SelectTrigger className="h-9 w-[200px]" data-testid="detail-stato"><SelectValue /></SelectTrigger>
              <SelectContent>{statusOptions.filter((s) => t.status === s.v || (s.v === "sospeso_manutenzione" ? t.area !== "manutenzione" && canManage : s.v !== "da_sbloccare")).map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {canManage && (
            <>
              <div className="space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Priorità</p>
                <Select value={t.priority} onValueChange={(v) => patch({ priority: v }, "Priorità aggiornata")}>
                  <SelectTrigger className="h-9 w-[130px]" data-testid="detail-priorita"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITY.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Tecnico</p>
                <Select value={t.assigned_to || "none"} onValueChange={(v) => patch({ assigned_to: v === "none" ? "" : v }, "Tecnico assegnato")}>
                  <SelectTrigger className="h-9 w-[160px]" data-testid="detail-tecnico"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Non assegnato</SelectItem>{tecnici.map((tc) => <SelectItem key={tc.id} value={tc.id}>{tc.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      </PageHeader>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica ticket {t.code}</DialogTitle></DialogHeader>
          {editForm && (
            <div className="grid gap-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Cliente</Label><Input data-testid="edit-cliente" value={editForm.client_name} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Telefono</Label><Input value={editForm.client_phone} onChange={(e) => setEditForm({ ...editForm, client_phone: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Committente</Label>
                  <Select value={editForm.committente_id || "none"} onValueChange={(v) => setEditForm({ ...editForm, committente_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="edit-committente"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Categoria</Label>
                  <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                    <SelectTrigger data-testid="edit-categoria"><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Indirizzo</Label><Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Comune</Label><Input value={editForm.comune} onChange={(e) => setEditForm({ ...editForm, comune: e.target.value })} /></div>
              </div>
              <div className="space-y-4">
                <div className="rounded-lg border border-border bg-secondary/30 p-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wider text-primary">Appuntamento cliente</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label>Data</Label><DateField testid="edit-data" value={editForm.scheduled_date} onChange={(v) => setEditForm({ ...editForm, scheduled_date: v })} /></div>
                    <div className="space-y-1.5"><Label>Orario</Label><TimeField testid="edit-appointment" value={editForm.appointment_time} onChange={(v) => setEditForm({ ...editForm, appointment_time: v })} /></div>
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-secondary/30 p-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wider text-primary">Intervento previsto</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1.5"><Label className="whitespace-nowrap">Data intervento</Label><DateField testid="edit-int-date" value={editForm.intervention_date} onChange={(v) => setEditForm({ ...editForm, intervention_date: v })} /></div>
                    <div className="space-y-1.5"><Label className="whitespace-nowrap">Ora inizio</Label><TimeField testid="edit-start" value={editForm.start_time} onChange={(v) => setEditForm({ ...editForm, start_time: v })} /></div>
                    <div className="space-y-1.5"><Label className="whitespace-nowrap">Durata (min)</Label><Input type="number" data-testid="edit-duration" value={editForm.duration_min} onChange={(e) => setEditForm({ ...editForm, duration_min: e.target.value })} /></div>
                  </div>
                  {editForm.intervention_date && editForm.scheduled_date && editForm.intervention_date !== editForm.scheduled_date && (
                    <p className="mt-2 text-xs font-semibold text-amber-700">Data intervento diversa dall'appuntamento ({editForm.intervention_date < editForm.scheduled_date ? "anticipato" : "posticipato"}).</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>ID OLO</Label><Input data-testid="edit-idolo" value={editForm.id_olo} onChange={(e) => setEditForm({ ...editForm, id_olo: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>ID Risorsa</Label><Input value={editForm.id_risorsa} onChange={(e) => setEditForm({ ...editForm, id_risorsa: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>PFS</Label><Input data-testid="edit-pfs" value={editForm.pfs} onChange={(e) => setEditForm({ ...editForm, pfs: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Picco</Label>
                  <Select value={editForm.picco || "none"} onValueChange={(v) => setEditForm({ ...editForm, picco: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="edit-picco"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{PICCO_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Descrizione</Label><Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button><Button onClick={saveEdit} data-testid="edit-save">Salva modifiche</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {t.area === "manutenzione" && t.source_ticket_id && (
        <MaintenanceBanner tone="mnt" text="Arrivato da Ticket - Delivery & Assurance" code={t.source_ticket_code} hint='Quando questo ticket viene completato, il ticket Delivery torna "Assegnato" se ha già un tecnico, altrimenti passa in "Da Sbloccare".' onOpen={() => navigate(`/tickets/${t.source_ticket_id}`, { state: { from: "/tickets" } })} />
      )}
      {t.area !== "manutenzione" && t.status === "sospeso_manutenzione" && t.maintenance_ticket_id && (
        <MaintenanceBanner tone="orig" text="Sospeso e inviato a Ticket - Manutenzione" code="Apri la copia in Manutenzione" hint="Puoi assegnarlo e pianificarlo a un tecnico Delivery (riceve l'ODT): alla chiusura della manutenzione torna Assegnato e il tecnico viene avvisato." onOpen={() => navigate(`/tickets/${t.maintenance_ticket_id}`, { state: { from: "/manutenzione" } })} />
      )}

      {related.length > 0 && <RelatedTimeline current={t} related={related} onOpen={(rid) => navigate(`/tickets/${rid}`, { state: { from: location.state?.from } })} />}

      <Tabs defaultValue="dettagli">
        <TabsList data-testid="detail-tabs">
          <TabsTrigger value="dettagli" data-testid="tab-dettagli">Dettagli intervento</TabsTrigger>
          <TabsTrigger value="consuntivo" data-testid="tab-consuntivo">Consuntivo</TabsTrigger>
          <TabsTrigger value="allegati" data-testid="tab-allegati">Allegati e storico</TabsTrigger>
        </TabsList>

        {/* DETTAGLI */}
        <TabsContent value="dettagli" className="mt-4">
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="mb-4 font-bold text-navy">Dettagli intervento</h3>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <Info icon={Hash} label="Codice" value={<span className="font-mono">{t.code}</span>} />
              <Info icon={Hash} label="ID OLO" value={t.id_olo} />
              <Info icon={Hash} label="ID Risorsa" value={t.id_risorsa} />
              <Info icon={Hash} label="PFS" value={t.pfs} />
              <Info icon={Tag} label="Picco" value={t.picco} />
              <Info icon={Building2} label="Committente" value={t.committente} />
              <Info icon={User} label="Cliente" value={t.client_name} />
              <Info icon={Phone} label="Telefono" value={t.client_phone} />
              <Info icon={MapPin} label="Indirizzo" value={t.address} />
              <Info icon={MapPin} label="Comune" value={t.comune} />
              <Info icon={Tag} label="Categoria" value={t.category} />
              <Info icon={User} label="Tecnico" value={t.assigned_to_name} />
              <Info icon={Calendar} label="Appuntamento cliente" value={t.scheduled_date ? (
                <span className={cn("inline-flex items-center gap-1.5", t.anticipo ? "rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 font-semibold text-amber-800" : "")}>
                  {t.anticipo && <AlertTriangle size={13} className="text-amber-600" />}{`${fmtDate(t.scheduled_date)} ${t.appointment_time || ""}`.trim()}
                  {t.anticipo && <span className="rounded-full bg-amber-200 px-1.5 text-[10px] font-bold uppercase text-amber-900" data-testid="detail-anticipo">{t.anticipo}</span>}
                </span>
              ) : null} />
              <Info icon={Clock} label="Intervento" value={(t.intervention_date || t.start_time) ? `${t.intervention_date ? fmtDate(t.intervention_date) : ""} ${t.start_time ? `${t.start_time}–${endTime(t)}` : ""}`.trim() : null} />
              <Info icon={Timer} label="Durata" value={t.duration_min ? `${t.duration_min} min` : null} />
              <Info icon={Clock} label="Stato" value={
                <span className="flex flex-wrap items-center gap-2">
                  <StatoBadge stato={t.status} />
                  {t.status === "sospeso" && t.suspension_reason && (
                    <span className="text-xs font-semibold text-red-600" data-testid="detail-suspension-reason">Motivo: {t.suspension_reason}</span>
                  )}
                  {t.status === "ko" && t.ko_reason && (
                    <span className="text-xs font-semibold text-red-800" data-testid="detail-ko-reason">Motivo: {t.ko_reason}</span>
                  )}
                </span>
              } />
              <Info icon={Clock} label="Priorità" value={<PrioritaBadge priorita={t.priority} />} />
              <Info icon={Calendar} label="Data creazione" value={fmtDate(t.created_at)} />
              <Info icon={Timer} label="Aging" value={`${t.aging_days} giorni`} />
              <Info icon={Receipt} label="Amministrazione" value={
                <div className="flex gap-1.5">
                  {t.billed && <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Contabilizzato</span>}
                  {t.paid && <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">Pagato</span>}
                  {!t.billed && !t.paid && <span className="text-sm text-muted-foreground">Da contabilizzare</span>}
                </div>
              } />
            </div>
            {t.description && <div className="mt-5 rounded-lg bg-secondary/50 p-4"><p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Descrizione</p><p className="mt-1 text-sm text-navy">{t.description}</p></div>}

            <div className="mt-5 grid gap-4 lg:grid-cols-3">
            <div className="rounded-lg border border-border p-4 lg:col-span-2">
              <div className="mb-3 flex items-center gap-2">
                <Boxes size={18} className="text-primary" />
                <h4 className="font-bold text-navy">Materiali da installare</h4>
                <span className="text-xs text-muted-foreground">(dal magazzino — suggeriti al tecnico)</span>
              </div>
              {(t.planned_materials || []).length ? (
                <div className="space-y-2">
                  {(t.planned_materials || []).map((m, idx) => {
                    const p = products.find((x) => x.id === m.product_id || x.sku === m.sku) || {};
                    const mc = m.manufacturer_code || p.manufacturer_code || "—";
                    const sn = m.supplier_name || p.supplier_name || "—";
                    return (
                    <div key={idx} className="flex items-center gap-3 rounded-lg border border-border bg-secondary/20 p-2.5" data-testid={`planned-${idx}`}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-navy">{m.name || m.description}</p>
                        <p className="font-mono text-xs text-muted-foreground">Cod. produttore: {mc} · Fornitore: {sn}</p>
                      </div>
                      {canManage ? (
                        <>
                          <Input type="number" min="1" className="h-8 w-20" value={m.qty} onChange={(e) => setPlannedQty(idx, e.target.value)} data-testid={`planned-qty-${idx}`} />
                          <span className="text-xs text-muted-foreground">{m.unit}</span>
                          <button onClick={() => delPlanned(idx)} className="text-muted-foreground hover:text-destructive" data-testid={`planned-del-${idx}`}><Trash2 size={16} /></button>
                        </>
                      ) : <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-bold text-navy">{m.qty} {m.unit}</span>}
                    </div>
                    );
                  })}
                </div>
              ) : <p className="text-sm text-muted-foreground">Nessun materiale pianificato.</p>}

              {canManage && (
                <div className="relative mt-3">
                  <div className="relative">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input className="pl-9" placeholder="Cerca prodotto dal magazzino da aggiungere..." value={pmSearch} onChange={(e) => setPmSearch(e.target.value)} data-testid="planned-search" />
                  </div>
                  {pmSearch && (
                    <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
                      {products.filter((p) => `${p.name} ${p.sku}`.toLowerCase().includes(pmSearch.toLowerCase())).slice(0, 40).map((p) => (
                        <button key={p.id} onClick={() => addPlanned(p)} data-testid={`product-opt-${p.sku}`} className="flex w-full items-center justify-between gap-2 border-b border-border/50 px-3 py-2 text-left last:border-0 hover:bg-secondary">
                          <span className="min-w-0"><span className="block truncate text-sm font-semibold text-navy">{p.name}</span><span className="block truncate font-mono text-xs text-muted-foreground">SKU: {p.sku || "—"} · Cod. produttore: {p.manufacturer_code || "—"} · Fornitore: {p.supplier_name || "—"}</span></span>
                          <Plus size={15} className="shrink-0 text-primary" />
                        </button>
                      ))}
                      {products.filter((p) => `${p.name} ${p.sku}`.toLowerCase().includes(pmSearch.toLowerCase())).length === 0 && (
                        <p className="px-3 py-2 text-sm text-muted-foreground">Nessun prodotto trovato</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Password apparati e ID Servizi */}
            <div className="rounded-lg border border-border p-4" data-testid="device-pwd-panel">
              <div className="mb-3 flex items-center gap-2">
                <Hash size={18} className="text-primary" />
                <h4 className="font-bold text-navy">Apparati &amp; Servizi</h4>
              </div>
              {canManage ? (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Password apparati</Label>
                    <Textarea rows={2} className="font-mono text-sm" placeholder="Password apparati…" value={devicePwd} onChange={(e) => setDevicePwd(e.target.value)} data-testid="device-pwd-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>ID Servizi</Label>
                    <Textarea rows={2} className="font-mono text-sm" placeholder="ID Servizi…" value={idServizi} onChange={(e) => setIdServizi(e.target.value)} data-testid="id-servizi-input" />
                  </div>
                  <Button size="sm" onClick={() => patch({ device_password: devicePwd, id_servizi: idServizi }, "Salvato")} data-testid="device-pwd-save"><Send size={14} />Salva</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="mb-1 text-xs font-semibold text-muted-foreground">Password apparati</p>
                    {t.device_password ? (
                      <div className="flex items-start gap-2">
                        <pre className="min-w-0 flex-1 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-secondary/30 p-2 font-mono text-sm text-navy" data-testid="device-pwd-value">{t.device_password}</pre>
                        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(t.device_password); toast.success("Copiato"); }} data-testid="device-pwd-copy"><Upload size={13} className="rotate-180" /></Button>
                      </div>
                    ) : <p className="text-sm text-muted-foreground">—</p>}
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-semibold text-muted-foreground">ID Servizi</p>
                    {t.id_servizi ? (
                      <div className="flex items-start gap-2">
                        <pre className="min-w-0 flex-1 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-secondary/30 p-2 font-mono text-sm text-navy" data-testid="id-servizi-value">{t.id_servizi}</pre>
                        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(t.id_servizi); toast.success("Copiato"); }} data-testid="id-servizi-copy"><Upload size={13} className="rotate-180" /></Button>
                      </div>
                    ) : <p className="text-sm text-muted-foreground">—</p>}
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>
        </TabsContent>

        {/* CONSUNTIVO */}
        <TabsContent value="consuntivo" className="mt-4 space-y-6">
          {/* Materiali */}
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2"><Package size={18} className="text-primary" /><h3 className="font-bold text-navy">Materiali utilizzati</h3></div>
              <div className="text-sm font-bold text-navy">Totale: € {matTotal.toFixed(2)}</div>
            </div>
            {t.stock_discharged && t.commessa_name && (
              <a href={t.commessa_url || "#"} target="_blank" rel="noreferrer" data-testid="commessa-link"
                className="mb-4 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm transition-colors hover:bg-emerald-100">
                <span className="font-semibold text-emerald-800">Materiali scaricati sul magazzino · Commessa <span className="font-mono">{t.commessa_name}</span></span>
                <span className="flex items-center gap-1 font-semibold text-emerald-700">Apri in Magazzino <ExternalLink size={14} /></span>
              </a>
            )}
            {(t.materials_used || []).length ? (
              <div className="mb-4 overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/40"><tr className="text-left">{["Codice", "Descrizione", "Q.tà", "Prezzo", "Tot.", ""].map((h) => <th key={h} className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{h}</th>)}</tr></thead>
                  <tbody>
                    {t.materials_used.map((m) => (
                      <tr key={m.id} className="border-t border-border/60" data-testid={`material-${m.id}`}>
                        <td className="px-3 py-2 font-mono text-primary">{m.manufacturer_code || m.code || m.sku || "—"}</td>
                        <td className="px-3 py-2 text-navy">{m.name || m.description}{m.supplier_name ? <span className="ml-1 text-xs text-muted-foreground">· {m.supplier_name}</span> : null}{(m.serial_numbers || []).length > 0 && <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">[{m.serial_numbers.join(", ")}]</span>}{substituteForName(m.product_id) && <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700" data-testid={`material-substitute-${m.id}`} title={`Pianificato: ${substituteForName(m.product_id)}`}><ArrowLeftRight size={10} />in sost. di {substituteForName(m.product_id)}</span>}</td>
                        <td className="px-3 py-2">{m.qty} {m.unit}</td>
                        <td className="px-3 py-2">€ {Number(m.price).toFixed(2)}</td>
                        <td className="px-3 py-2 font-semibold text-navy">€ {(m.price * m.qty).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right"><button onClick={() => delMaterial(m.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="mb-4 text-sm text-muted-foreground">Nessun materiale registrato</p>}
            <div className="rounded-lg border border-border bg-secondary/20 p-3">
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Aggiungi materiale dal magazzino del tecnico</p>
              {!t.assigned_to ? (
                <p className="text-sm text-muted-foreground">Assegna un tecnico per selezionare i materiali dal suo furgone.</p>
              ) : warehouseProducts.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nessun materiale in giacenza nel magazzino del tecnico assegnato.</p>
              ) : (
                <>
                  <div className="grid grid-cols-12 items-end gap-2">
                    <div className="col-span-6 space-y-1">
                      <Label className="text-xs">Materiale</Label>
                      <ProductCombobox products={warehouseProducts} value={selProd} onChange={(v) => { setSelProd(v); setWSerials([]); setWQty(1); }} testid="mat-prod" />
                    </div>
                    {curProd && !curProd.is_serialized && (
                      <div className="col-span-2 space-y-1"><Label className="text-xs">Q.tà</Label><Input type="number" min="1" value={wQty} onChange={(e) => setWQty(e.target.value)} data-testid="mat-qty" /></div>
                    )}
                    <Button className="col-span-2" onClick={addFromWarehouse} data-testid="mat-add"><Plus size={16} />Aggiungi</Button>
                  </div>
                  {curProd && curProd.is_serialized && (
                    <div className="mt-2">
                      <p className="mb-1 text-xs font-semibold text-muted-foreground">Seriali dal magazzino ({wSerials.length} selez.)</p>
                      <div className="flex flex-wrap gap-1.5">
                        {curProd.serials.map((s) => (
                          <button key={s} onClick={() => toggleWSerial(s)} data-testid={`mat-serial-${s}`}
                            className={cn("rounded-md border px-2 py-1 font-mono text-xs transition-colors", wSerials.includes(s) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-navy")}>{s}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {canManage && (
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="mb-4 flex items-center gap-2"><Receipt size={18} className="text-primary" /><h3 className="font-bold text-navy">Benestare & Fatturazione</h3></div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1.5"><Label>N° Benestare</Label><Input value={t.benestare_number || ""} onChange={(e) => setT({ ...t, benestare_number: e.target.value })} onBlur={(e) => patch({ benestare_number: e.target.value })} data-testid="benestare-num" /></div>
                <div className="space-y-1.5"><Label>Data Benestare</Label><DateField testid="benestare-date" value={t.benestare_date || ""} onChange={(v) => patch({ benestare_date: v })} /></div>
                <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2"><span className="text-sm font-semibold text-navy">Contabilizzato</span><Switch checked={!!t.billed} onCheckedChange={(v) => patch({ billed: v }, "Aggiornato")} data-testid="billed-switch" /></div>
                <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2"><span className="text-sm font-semibold text-navy">Pagato</span><Switch checked={!!t.paid} onCheckedChange={(v) => { if (v) { setBenForm({ benestare_number: t.benestare_number || "", benestare_date: t.benestare_date || "" }); setPaidDialog(true); } else { patch({ paid: false }, "Aggiornato"); } }} data-testid="paid-switch" /></div>
              </div>

              <Dialog open={paidDialog} onOpenChange={setPaidDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Dati Benestare</DialogTitle></DialogHeader>
                  <div className="grid gap-4 py-2">
                    <p className="text-sm text-muted-foreground">Inserisci i dati del benestare per segnare il ticket come pagato.</p>
                    <div className="space-y-1.5"><Label>N° Benestare</Label><Input value={benForm.benestare_number} onChange={(e) => setBenForm({ ...benForm, benestare_number: e.target.value })} data-testid="paid-benestare-num" /></div>
                    <div className="space-y-1.5"><Label>Data Benestare</Label><DateField testid="paid-benestare-date" value={benForm.benestare_date} onChange={(v) => setBenForm({ ...benForm, benestare_date: v })} /></div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setPaidDialog(false)}>Annulla</Button>
                    <Button onClick={() => { patch({ paid: true, benestare_number: benForm.benestare_number, benestare_date: benForm.benestare_date }, "Segnato come pagato"); setPaidDialog(false); }} data-testid="paid-confirm">Conferma pagato</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </TabsContent>

        {/* ALLEGATI E STORICO */}
        <TabsContent value="allegati" className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-6">
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2"><ImageIcon size={18} className="text-primary" /><h3 className="font-bold text-navy">Allegati / Foto</h3></div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="att-input" />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="att-upload"><Upload size={16} />Carica foto</Button>
              </div>
              {atts.length ? (
                <div className="grid grid-cols-3 gap-3">
                  {atts.map((a, idx) => (
                    <div key={a.id} className="group relative cursor-zoom-in overflow-hidden rounded-lg border border-border" data-testid={`att-${a.id}`} onClick={() => setLightbox(idx)}>
                      <img src={attUrl(a)} alt={a.filename} className="h-28 w-full object-cover transition-transform group-hover:scale-105" />
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/25">
                        <ZoomIn size={20} className="text-white opacity-0 transition-opacity group-hover:opacity-100" />
                      </div>
                      {a.source === "checklist" && <span className="absolute bottom-1 left-1 rounded bg-primary/90 px-1.5 py-0.5 text-[9px] font-bold text-primary-foreground">Check-list</span>}
                      <button onClick={(e) => { e.stopPropagation(); delAtt(a.id); }} data-testid={`att-del-${a.id}`} className="absolute right-1 top-1 rounded-md bg-white/90 p-1 text-destructive opacity-0 transition-opacity group-hover:opacity-100"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Nessuna foto caricata</p>}
            </div>

            {/* Note */}
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="mb-4 flex items-center gap-2"><MessageSquare size={18} className="text-primary" /><h3 className="font-bold text-navy">Note</h3></div>
              <div className="mb-4 flex gap-2">
                <Textarea rows={2} placeholder="Scrivi una nota (tecnico o gestore)..." value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="note-input" />
                <Button onClick={addNote} data-testid="note-add" className="self-end"><Send size={16} /></Button>
              </div>
              <div className="space-y-3">
                {(t.notes || []).length ? t.notes.slice().reverse().map((n) => (
                  <div key={n.id} className="rounded-lg border border-border bg-secondary/30 p-3" data-testid={`note-${n.id}`}>
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-sm font-bold text-navy">{n.author}</span>
                      <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">{ROLE_LABEL[n.role] || n.role}</span>
                      <span className="text-xs text-muted-foreground">{fmtDateTime(n.at)}</span>
                      {n.edited_at && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700" title={`Modificata${n.edited_by ? ` da ${n.edited_by}` : ""} il ${fmtDateTime(n.edited_at)}`}>modificata{n.edited_by ? ` da ${n.edited_by}` : ""} · {fmtDateTime(n.edited_at)}</span>}
                      {canModifyNote(n) && editNoteId !== n.id && (
                        <span className="ml-auto flex items-center gap-1">
                          <button onClick={() => { setEditNoteId(n.id); setEditNoteText(n.text); }} className="text-muted-foreground hover:text-primary" data-testid={`note-edit-${n.id}`}><Pencil size={14} /></button>
                          <button onClick={() => deleteNote(n.id)} className="text-muted-foreground hover:text-destructive" data-testid={`note-del-${n.id}`}><Trash2 size={14} /></button>
                        </span>
                      )}
                    </div>
                    {editNoteId === n.id ? (
                      <div className="mt-1 flex gap-2">
                        <Textarea rows={2} value={editNoteText} onChange={(e) => setEditNoteText(e.target.value)} data-testid={`note-edit-input-${n.id}`} />
                        <div className="flex flex-col gap-1">
                          <Button size="sm" onClick={() => saveNoteEdit(n.id)} data-testid={`note-edit-save-${n.id}`}>Salva</Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditNoteId(null); setEditNoteText(""); }} data-testid={`note-edit-cancel-${n.id}`}>Annulla</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-navy">{n.text}</p>
                    )}
                  </div>
                )) : <p className="text-sm text-muted-foreground">Nessuna nota</p>}
              </div>

              {canManage && (
                <div className="mt-4 border-t border-border pt-3">
                  <button onClick={toggleDeleted} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-navy" data-testid="deleted-notes-toggle">
                    <History size={13} />
                    Note eliminate {showDeleted ? "▲" : "▼"}
                  </button>
                  {showDeleted && (
                    <div className="mt-3 space-y-2" data-testid="deleted-notes-list">
                      {deletedNotes.length ? deletedNotes.map((n) => (
                        <div key={n.id} className="rounded-lg border border-dashed border-border bg-muted/40 p-3" data-testid={`deleted-note-${n.id}`}>
                          <div className="mb-1 flex flex-wrap items-center gap-2">
                            <span className="text-sm font-bold text-navy line-through">{n.author}</span>
                            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">{ROLE_LABEL[n.role] || n.role}</span>
                            <span className="text-xs text-muted-foreground">creata {fmtDateTime(n.at)}</span>
                            <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">eliminata da {n.deleted_by} · {fmtDateTime(n.deleted_at)}</span>
                          </div>
                          <p className="text-sm text-muted-foreground line-through">{n.text}</p>
                        </div>
                      )) : <p className="text-sm text-muted-foreground">Nessuna nota eliminata</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Storico */}
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="mb-4 flex items-center gap-2"><History size={18} className="text-primary" /><h3 className="font-bold text-navy">Storico stati</h3></div>
            <ol className="relative space-y-4 border-l-2 border-border pl-5">
              {(t.history || []).slice().reverse().map((h, i) => (
                <li key={i} className="relative" data-testid={`history-${i}`}>
                  <span className="absolute -left-[27px] top-1 h-3 w-3 rounded-full border-2 border-white bg-primary" />
                  <div className="flex flex-wrap items-center gap-2">
                    <StatoBadge stato={h.status} />
                    <span className="text-sm font-semibold text-navy">{h.by}</span>
                    <span className="text-xs text-muted-foreground">{fmtDateTime(h.at)}</span>
                  </div>
                  {h.note && <p className="mt-1 text-sm text-muted-foreground">{h.note}</p>}
                </li>
              ))}
            </ol>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Sospendi ticket {t.code}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label>Motivo sospensione *</Label>
              <Select value={suspendMotivo} onValueChange={setSuspendMotivo}>
                <SelectTrigger data-testid="suspend-motivo"><SelectValue placeholder="Seleziona un motivo…" /></SelectTrigger>
                <SelectContent>{suspensionReasons.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Nota *</Label>
              <Textarea placeholder="Descrivi la sospensione (verrà salvata nello storico)…" value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} data-testid="suspend-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendOpen(false)}>Annulla</Button>
            <Button onClick={submitSuspend} data-testid="suspend-confirm">Sospendi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={koOpen} onOpenChange={setKoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Imposta KO · {t.code}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label>Motivo del KO *</Label>
              <Select value={koMotivo} onValueChange={setKoMotivo}>
                <SelectTrigger data-testid="ko-motivo"><SelectValue placeholder="Seleziona un motivo…" /></SelectTrigger>
                <SelectContent>{koReasons.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Nota *</Label>
              <Textarea placeholder="Descrivi il motivo del KO (verrà salvato nello storico)…" value={koNote} onChange={(e) => setKoNote(e.target.value)} data-testid="ko-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKoOpen(false)}>Annulla</Button>
            <Button onClick={submitKo} data-testid="ko-confirm">Conferma KO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AttachmentLightbox attachments={atts} index={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
